import {
  Box,
  PayoutIcon,
  Drawer,
  ExchangeIcon,
  ExitIcon,
  HomeIcon,
  IconButton,
  List,
  MenuIcon,
  PlusFilledIcon,
  ProfileIcon,
  SendIcon,
  SettingsIcon,
  Stack,
  SwitchIcon,
  Theme,
  TransactionIcon,
  useMediaQuery,
  StandingOrderIcon,
  FriendsIcon,
  AccountIcon,
  CardIcon,
  VaultIcon,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { NavItem } from './NavItem';
import { useLogoutMutation } from '../../api/auth';
import { useCallback, useEffect, useMemo } from 'react';
import { useBoolean } from 'usehooks-ts';
import { useRouterState } from '@tanstack/react-router';
import { useBusinessQuery } from '../../api/businesses';
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey';
import { ProfileButton } from './ProfileButton';
import { useHasRole } from '../../api/access';
import { useCustomerQuery, useIsCardsAvailable, useIsVaultsAvailable } from '../../api/customers';

export function Sidebar() {
  const { t } = useTranslation();
  const customerQuery = useCustomerQuery();
  const businessQuery = useBusinessQuery();
  const isPayer = useHasRole('PAYER');
  const isViewer = useHasRole('VIEWER');
  const isCardsAvailable = useIsCardsAvailable();
  const isVaultsAvailable = useIsVaultsAvailable();

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const drawerState = useBoolean(false);

  const isTransitioning = useRouterState({
    select: (state) => state.status === 'pending',
  });

  const closeDrawer = drawerState.setFalse;
  useEffect(() => {
    if (isTransitioning) {
      closeDrawer();
    }
  }, [isTransitioning, closeDrawer]);

  const idempotencyKey = useIdempotencyKey();
  const logoutMutation = useLogoutMutation({ idempotencyKey });
  const handleLogout = useCallback(() => logoutMutation.mutate(), [logoutMutation]);

  const isActive =
    businessQuery.data?.business.state === 'VERIFIED' &&
    businessQuery.data?.profile.state === 'ACTIVE';

  const drawer = (
    <>
      <Stack flexGrow={1}>
        <List>
          <NavItem href="/account/home" title={t('Home')} icon={<HomeIcon />} />
        </List>
        {isActive ? (
          <List>
            <NavItem href="/account/topup" title={t('Top up')} icon={<PlusFilledIcon />} />
            {isPayer ? (
              <>
                <NavItem
                  href="/account/exchange/start"
                  title={t('Exchange')}
                  icon={<ExchangeIcon />}
                />
                <NavItem href="/account/payouts" title={t('Payouts')} icon={<PayoutIcon />} />
                <NavItem href="/account/transfer/start" title={t('Transfer')} icon={<SendIcon />} />
              </>
            ) : null}
            <NavItem
              href="/account/transactions"
              title={t('Transactions')}
              icon={<TransactionIcon />}
            />
            {isViewer ? (
              <>
                <NavItem href="/account/accounts" title={t('Accounts')} icon={<AccountIcon />} />
                {isVaultsAvailable ? (
                  <NavItem
                    href="/account/vaults"
                    title={t('Earning vaults')}
                    icon={<VaultIcon />}
                  />
                ) : null}
                {isCardsAvailable ? (
                  <NavItem href="/account/cards" title={t('Cards')} icon={<CardIcon />} />
                ) : null}
                <NavItem
                  href="/account/standing-orders"
                  title={t('Standing orders')}
                  icon={<StandingOrderIcon />}
                />
              </>
            ) : null}
          </List>
        ) : null}
        <List>
          {isActive && isViewer ? (
            <>
              <NavItem href="/account/business" title={t('Business')} icon={<ProfileIcon />} />
              <NavItem href="/account/members" title={t('Linked members')} icon={<FriendsIcon />} />
            </>
          ) : null}
          <NavItem href="/account/settings" title={t('Settings')} icon={<SettingsIcon />} />
        </List>
      </Stack>
      <List>
        <NavItem href="/switch" title={t('Switch business')} icon={<SwitchIcon />} />
        <NavItem title={t('Sign out')} icon={<ExitIcon />} onClick={handleLogout} />
      </List>
    </>
  );

  const businessName =
    businessQuery.data?.profile.name ?? businessQuery.data?.business.name ?? undefined;
  const customerName = useMemo(() => {
    if (!customerQuery.data) return undefined;
    return (
      customerQuery.data.profile.name ||
      [customerQuery.data.individual.name?.firstName, customerQuery.data.individual.name?.lastName]
        .filter(Boolean)
        .join(' ') ||
      customerQuery.data.identity.email ||
      customerQuery.data.identity.unconfirmedEmail ||
      undefined
    );
  }, [customerQuery.data]);

  const logo = <ProfileButton businessName={businessName} customerName={customerName} />;

  if (isMobile) {
    return (
      <Box component="nav" paddingX={2} paddingY={4}>
        <Stack direction="row" justifyContent="space-between">
          {logo}
          <Box>
            <IconButton onClick={drawerState.setTrue}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Stack>
        <Drawer anchor="left" open={drawerState.value} onClose={drawerState.setFalse}>
          <Box
            width="75vw"
            height="100%"
            maxWidth={336}
            padding={4}
            display="flex"
            flexDirection="column"
          >
            {drawer}
          </Box>
        </Drawer>
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      width={336}
      height="100%"
      display="flex"
      flexDirection="column"
      paddingX={4}
      paddingY={8}
    >
      <Box sx={{ mb: 2 }}>{logo}</Box>
      {drawer}
    </Box>
  );
}
